import { SupportAiBillingVersion } from "@redotech/redo-billing-common/support-ai-billing-version";
import {
  OPENAI_MODELS,
  OpenAIModel,
} from "@redotech/redo-support-settings-common/openai-models";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card, CardSize } from "@redotech/redo-web/card";
import { MaybeDate } from "@redotech/redo-web/date-picker";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { areDatesEqual } from "@redotech/util/date";
import {
  dateToPlainDateTime,
  plainDateTimeToDate,
} from "@redotech/util/temporal";
import { memo, useEffect, useMemo } from "react";

export const aiBillingForm = groupInput({
  billingVersion: input<SupportAiBillingVersion | null>(),
  copilotModel: input<OpenAIModel | null>(),
  freeTrialEndDate: input<MaybeDate>({ equal: areDatesEqual }),
  oneTimeFreeResolutions: input<string>(),
  recurringFreeResolutions: input<string>(),
  recurringFreeResolutionsEndDate: input<MaybeDate>({ equal: areDatesEqual }),
  resolutionPercentageDiscount: input<string>(),
  resolutionPercentageDiscountEndDate: input<MaybeDate>({
    equal: areDatesEqual,
  }),
  aiResolutionPriceInCents: input<string>(),
  pricePerOrderInCents: input<string>(),
});

export type AiBillingForm = InputSchema.Input<typeof aiBillingForm>;

export type AiBillingValue = InputSchema.Value<typeof aiBillingForm>;

export const aiBillingDefault: AiBillingValue = {
  billingVersion: null,
  copilotModel: null,
  freeTrialEndDate: null,
  oneTimeFreeResolutions: "0",
  recurringFreeResolutions: "0",
  recurringFreeResolutionsEndDate: null,
  resolutionPercentageDiscount: "0",
  resolutionPercentageDiscountEndDate: null,
  aiResolutionPriceInCents: "0",
  pricePerOrderInCents: "0",
};

export const AiBillingCard = memo(function AiBillingCard({
  input,
  onValidationChange,
}: {
  input: AiBillingForm;
  onValidationChange?: (hasErrors: boolean) => void;
}) {
  const {
    billingVersion,
    copilotModel,
    freeTrialEndDate,
    oneTimeFreeResolutions,
    recurringFreeResolutions,
    recurringFreeResolutionsEndDate,
    resolutionPercentageDiscount,
    resolutionPercentageDiscountEndDate,
    aiResolutionPriceInCents,
    pricePerOrderInCents,
  } = input.inputs;

  const billingVersionOptions = useMemo(() => {
    const billingVersionRecord: Record<
      SupportAiBillingVersion,
      {
        id: SupportAiBillingVersion;
        type: "text";
        value: SupportAiBillingVersion;
        text: string;
      }
    > = {
      [SupportAiBillingVersion.BUCKETED_USAGE]: {
        id: SupportAiBillingVersion.BUCKETED_USAGE,
        type: "text" as const,
        value: SupportAiBillingVersion.BUCKETED_USAGE,
        text: "Bucketed Usage",
      },
      [SupportAiBillingVersion.SIMPLE_AI_USAGE]: {
        id: SupportAiBillingVersion.SIMPLE_AI_USAGE,
        type: "text" as const,
        value: SupportAiBillingVersion.SIMPLE_AI_USAGE,
        text: "Simple Usage (per-ticket)",
      },
      [SupportAiBillingVersion.PER_ORDER]: {
        id: SupportAiBillingVersion.PER_ORDER,
        type: "text" as const,
        value: SupportAiBillingVersion.PER_ORDER,
        text: "Per Order",
      },
    };

    return Object.values(billingVersionRecord);
  }, []);

  const selectedBillingVersionOption = useMemo(
    () =>
      billingVersionOptions.find((opt) => opt.value === billingVersion.value),
    [billingVersionOptions, billingVersion.value],
  );

  const copilotModelOptions = useMemo(() => {
    return OPENAI_MODELS.map((model) => ({
      id: model,
      type: "text" as const,
      value: model,
      text: model,
    }));
  }, []);

  const selectedCopilotModelOption = useMemo(
    () => copilotModelOptions.find((opt) => opt.value === copilotModel.value),
    [copilotModelOptions, copilotModel.value],
  );

  const recurringResolutionsError = useMemo(() => {
    const hasRecurringResolutions = Number(recurringFreeResolutions.value) > 0;
    const hasEndDate = recurringFreeResolutionsEndDate.value !== null;

    if (hasRecurringResolutions && !hasEndDate) {
      return "An end date is required when setting recurring free resolutions";
    }
    return null;
  }, [recurringFreeResolutions.value, recurringFreeResolutionsEndDate.value]);

  const percentageDiscountError = useMemo(() => {
    const hasDiscount = Number(resolutionPercentageDiscount.value) > 0;
    const hasEndDate = resolutionPercentageDiscountEndDate.value !== null;

    if (hasDiscount && !hasEndDate) {
      return "An end date is required when setting a percentage discount";
    }
    return null;
  }, [
    resolutionPercentageDiscount.value,
    resolutionPercentageDiscountEndDate.value,
  ]);

  const hasValidationErrors = Boolean(
    recurringResolutionsError || percentageDiscountError,
  );

  useEffect(() => {
    onValidationChange?.(hasValidationErrors);
  }, [hasValidationErrors, onValidationChange]);

  return (
    <Flex dir="column" gap="xl">
      <Card radius="sm" size={CardSize.MEDIUM} title="AI Billing">
        <RedoSingleSelectDropdownInput<SupportAiBillingVersion>
          label="AI Billing Version"
          options={billingVersionOptions}
          optionSelected={({ item }) => {
            billingVersion.setValue(item.value);
          }}
          placeholder="Select billing version (default: Bucketed Usage)"
          selectedItem={selectedBillingVersionOption}
          size="xs"
        />
        {billingVersion.value === SupportAiBillingVersion.SIMPLE_AI_USAGE && (
          <RedoTextInput
            description="Flat price per AI resolution in cents"
            label="AI resolution price (in cents)"
            min={0}
            setValue={aiResolutionPriceInCents.setValue}
            size="sm"
            step={1}
            value={aiResolutionPriceInCents.value}
          />
        )}
        {billingVersion.value === SupportAiBillingVersion.PER_ORDER && (
          <>
            <RedoAlert
              color="default"
              description="This brand will be billed per order using the same logic as checkout optimization billing."
              showCloseButton={false}
              title="Per Order Billing"
            />
            <RedoTextInput
              description="Price charged per qualifying order in cents"
              label="Price per order (in cents)"
              min={0}
              setValue={pricePerOrderInCents.setValue}
              size="sm"
              step={1}
              value={pricePerOrderInCents.value}
            />
          </>
        )}
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium">
            Free trial end date
          </Text>
          <RedoSingleDatePicker
            allowNoDateOption
            buttonSize="sm"
            date={
              freeTrialEndDate.value
                ? dateToPlainDateTime(freeTrialEndDate.value).toPlainDate()
                : undefined
            }
            setDate={(plainDate) =>
              freeTrialEndDate.setValue(
                plainDateTimeToDate(plainDate.toPlainDateTime("00:00")),
              )
            }
            showStatus
          />
          <Text fontSize="xs" textColor="tertiary">
            AI features will be enabled without billing acceptance until this
            date
          </Text>
        </Flex>

        <RedoTextInput
          label="One-time free resolutions"
          min={0}
          setValue={oneTimeFreeResolutions.setValue}
          size="sm"
          step={1}
          value={oneTimeFreeResolutions.value}
        />

        <RedoTextInput
          label="Recurring free resolutions"
          min={0}
          setValue={recurringFreeResolutions.setValue}
          size="sm"
          step={1}
          value={recurringFreeResolutions.value}
        />

        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium">
            Recurring free resolutions end date
          </Text>
          <RedoSingleDatePicker
            allowNoDateOption
            buttonSize="sm"
            date={
              recurringFreeResolutionsEndDate.value
                ? dateToPlainDateTime(
                    recurringFreeResolutionsEndDate.value,
                  ).toPlainDate()
                : undefined
            }
            setDate={(plainDate) =>
              recurringFreeResolutionsEndDate.setValue(
                plainDateTimeToDate(plainDate.toPlainDateTime("00:00")),
              )
            }
            showStatus
          />
          {recurringResolutionsError && (
            <Text fontSize="xs" textColor="error">
              {recurringResolutionsError}
            </Text>
          )}
        </Flex>

        <RedoTextInput
          IconTrailing={() => <span>%</span>}
          label="Resolution percentage discount"
          max={100}
          min={0}
          setValue={resolutionPercentageDiscount.setValue}
          size="sm"
          step={1}
          value={resolutionPercentageDiscount.value}
        />

        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium">
            Discount % end date
          </Text>
          <RedoSingleDatePicker
            allowNoDateOption
            buttonSize="sm"
            date={
              resolutionPercentageDiscountEndDate.value
                ? dateToPlainDateTime(
                    resolutionPercentageDiscountEndDate.value,
                  ).toPlainDate()
                : undefined
            }
            setDate={(plainDate) =>
              resolutionPercentageDiscountEndDate.setValue(
                plainDateTimeToDate(plainDate.toPlainDateTime("00:00")),
              )
            }
            showStatus
          />
          {percentageDiscountError && (
            <Text fontSize="xs" textColor="error">
              {percentageDiscountError}
            </Text>
          )}
        </Flex>
      </Card>
      <Card radius="sm" size={CardSize.MEDIUM} title="AI Settings">
        <RedoSingleSelectDropdownInput<OpenAIModel>
          label="Copilot Model"
          options={copilotModelOptions}
          optionSelected={({ item }) => {
            copilotModel.setValue(item.value);
          }}
          placeholder="Select copilot model (default: gpt-4.1-mini)"
          selectedItem={selectedCopilotModelOption}
          size="xs"
        />
        <Text fontSize="xs" textColor="tertiary">
          The LLM model used for copilot response suggestions
        </Text>
      </Card>
    </Flex>
  );
});
